import { useMemo } from 'react';

import {
  buildEntitiesWithBadgeMap,
  getShouldShowMenuObjectBadge,
} from 'features/menu/hooks/use-accelerator-badges-on-menu/utils';

import { IPickerOption, MenuObject } from '@rbi-ctg/menu';
import { useAcceleratorBadgesOnMenuExperiment } from 'experiments/use-accelerator-badges-on-menu-experiment';
import {
  IPricingAndAvailabilityFragment,
  useLoyaltyUserBestPromotionQuery,
} from 'generated/graphql-gateway';
import { useSystemwidePromotionQuery } from 'generated/sanity-graphql';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { isPicker } from 'utils/menu';

interface IUseAcceleratorBadgesOnMenuProps {
  menuData: MenuObject[];
  skipExposureLogging: boolean;
  getPricingAndAvailability?: (id: string) => IPricingAndAvailabilityFragment | null | undefined;
  isFirstPickerAspect?: boolean;
}

export type AcceleratorBadgeType = 'pointsMultiplier' | 'bonusPoints';

export interface IUseAcceleratorBadgesOnMenu {
  badges: Set<string>;
  pointsMultiplier: number;
  bonusPoints: number;
  shouldShowFullMenuBadge: boolean;
  shouldShowMenuObjectBadge: boolean;
  experimentEnabled: boolean;
  cartRequirement: Set<string>;
  promotionId: string;
}

export const useAcceleratorBadgesOnMenu = ({
  menuData,
  skipExposureLogging = false,
  getPricingAndAvailability,
  isFirstPickerAspect = false,
}: IUseAcceleratorBadgesOnMenuProps): IUseAcceleratorBadgesOnMenu => {
  // Log exposure
  const experimentEnabled = useAcceleratorBadgesOnMenuExperiment(skipExposureLogging);

  const loyaltyEnabled = useIsLoyaltyEnabled();
  const { loyaltyUserId, loading } = useLoyaltyUserId();

  const { data: bestPromotionData } = useLoyaltyUserBestPromotionQuery({
    variables: { loyaltyId: loyaltyUserId },
    skip: !loyaltyEnabled || !loyaltyUserId || !experimentEnabled,
    fetchPolicy: 'cache-and-network',
  });

  const promotionId = bestPromotionData?.loyaltyUserV2?.bestPromotion?.sanityId ?? '';

  const skip = !loyaltyEnabled || loading || !promotionId || !experimentEnabled;

  const { data } = useSystemwidePromotionQuery({
    variables: {
      id: promotionId,
    },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const sanityCartRequirements = useMemo(() => {
    return (
      data?.SystemwidePromotion?.systemwidePromotion?.find(
        req => req?.__typename === 'CartRequirement'
      ) || {}
    );
  }, [data?.SystemwidePromotion?.systemwidePromotion]);

  const skipAvailabilityCheck = !getPricingAndAvailability;

  const hasPaymentMethodRuleSet = useMemo(
    () =>
      !!data?.SystemwidePromotion?.systemwidePromotion?.find(
        req => req?.__typename === 'PaymentMethod'
      ),
    [data?.SystemwidePromotion?.systemwidePromotion]
  );

  const hasCartRequirement = 'cartRequirement' in sanityCartRequirements;

  const cartRequirement: Set<string> = useMemo(() => {
    if (hasCartRequirement && !hasPaymentMethodRuleSet) {
      return new Set(
        sanityCartRequirements.cartRequirement
          ?.map(cartItemRequirement => {
            // Checks for cart item requirement availability
            const _id = cartItemRequirement?.item?._id ?? '';
            // @TODO: update availability check in favor of One menu once the experiment is rolled out
            const isCartItemRequirementAvailable =
              skipAvailabilityCheck || !!getPricingAndAvailability?.(_id)?.isAvailable;
            return isCartItemRequirementAvailable ? _id : '';
          })
          .filter(Boolean) ?? []
      );
    }
    return new Set([]);
  }, [
    getPricingAndAvailability,
    hasCartRequirement,
    sanityCartRequirements,
    skipAvailabilityCheck,
    hasPaymentMethodRuleSet,
  ]);

  const badges = useMemo(() => buildEntitiesWithBadgeMap(menuData, cartRequirement), [
    cartRequirement,
    menuData,
  ]);

  const pointsMultiplier = data?.SystemwidePromotion?.pointsMultiplier ?? 0;
  const bonusPoints = data?.SystemwidePromotion?.bonusPoints ?? 0;

  const shouldShowFullMenuBadge = useMemo(() => {
    return !!(
      promotionId &&
      (pointsMultiplier || bonusPoints) &&
      (!hasCartRequirement || sanityCartRequirements.cartRequirement?.length === 0)
    );
  }, [bonusPoints, hasCartRequirement, pointsMultiplier, promotionId, sanityCartRequirements]);

  const shouldShowMenuObjectBadge = useMemo(() => {
    const menuObject = menuData[0];

    const shouldShowMenuBadge = getShouldShowMenuObjectBadge(menuObject, cartRequirement);

    // If the menu object is item or combo
    if (!isPicker(menuObject)) {
      return shouldShowMenuBadge;
    }

    // if the menu object is a picker
    const [firstAspect, secondAspect] = menuObject.pickerAspects;
    const firstAspectOptions = firstAspect?.pickerAspectOptions || [];
    const secondAspectOptions = secondAspect?.pickerAspectOptions || [];
    const productOptions = menuObject.options as IPickerOption[];

    const productHasMatchingMappingsWithBadge = (
      firstOptionId: string,
      secondOptionId: string
    ): boolean => {
      return productOptions.some(product => {
        const productMappingIds = product.pickerItemMappings.map(
          mapping => mapping.pickerAspectValueIdentifier
        );

        const hasBothMappings =
          productMappingIds.includes(firstOptionId) && productMappingIds.includes(secondOptionId);

        return hasBothMappings && badges.has(product.option._id);
      });
    };

    const areAllFirstAspectOptionsQualified = firstAspectOptions.every(firstOption =>
      secondAspectOptions.some(secondOption =>
        productHasMatchingMappingsWithBadge(firstOption.identifier, secondOption.identifier)
      )
    );

    return shouldShowMenuBadge || (areAllFirstAspectOptionsQualified && isFirstPickerAspect);
  }, [badges, cartRequirement, isFirstPickerAspect, menuData]);

  return {
    experimentEnabled,
    shouldShowFullMenuBadge,
    shouldShowMenuObjectBadge,
    badges,
    pointsMultiplier,
    bonusPoints,
    cartRequirement,
    promotionId,
  };
};
